if (typeof gsap !== 'undefined') {
  gsap.registerPlugin(SplitText);
}

if (customElements && !customElements.get('rolling-text')) {
  class RollingText extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.closest('a, button, [data-trigger-rolling-text]');

      this.animationTime = +this.dataset.animateSpeed || 0.4;
      this.timeline = null;
    }

    connectedCallback() {
      // CKC perf (Oct 16 2026): set up once. GSAP pins move sections around the page, which
      // re-fires connectedCallback; re-splitting the text every time froze phones for seconds.
      // Touch screens never hover, so the hover-only effect is skipped there entirely.
      if (this._ckcReady) return;
      if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;
      if (!this.trigger || typeof SplitText != 'function') {
        console.error('Not found: trigger or SplitText');
        return;
      }

      this._ckcReady = true;
      this.setupAnimation();
      this.bindEvents();
    }

    setupAnimation() {
      const mainTextLayer = this.querySelector('[data-role="rolling-text-main"]');
      if (!mainTextLayer) {
        console.error('Not found: mainTextLayer');
        return;
      }

      const copyTextLayers = [...this.querySelectorAll('[data-role="rolling-text-copy-layers"]')];
      if (!copyTextLayers.length) {
        console.error('Not found: copyTextLayers');
        return;
      }

      const splitMainText = SplitText.create(
        mainTextLayer,
        {
          type: 'words, chars',
          charsClass: 'char',
          wordsClass: 'word'
        }
      );

      const splitCopyTextLayers = copyTextLayers
        .map(line => {
          return SplitText.create(
            line,
            {
              type: 'words, chars',
              charsClass: 'char',
              wordsClass: 'word'
            });
        });

      /* === Config === */
      const depth = -(this.trigger.offsetHeight * 0.55);
      const transformOrigin = `-50% -50% ${depth}`;

      const lengthChars = splitMainText.chars.length - 1;
      const stagger = 0.45 / Math.pow(splitMainText.chars.length, 1.15);
      const totalCharsAnimationDuration = lengthChars * stagger + this.animationTime;

      gsap.set(
        [copyTextLayers, mainTextLayer],
        {
          perspective: 100,
          transformStyle: 'preserve-3d'
        }
      );


      /* === Setup animate === */
      this.timeline = gsap.timeline({ paused: true, id: 'rolling-text' });

      // start main layers
      this.timeline.fromTo(
        splitMainText.chars,
        {
          rotationX: 0
        },
        {
          rotationX: 90,
          stagger: {
            each: stagger
          },
          duration: this.animationTime * 0.65,
          ease: 'none',
          transformOrigin: `50% 50% ${depth}`
        }
      );

      // animate copy layers
      splitCopyTextLayers.forEach((split, index) => {
        const container = split.elements[0];

        this.timeline.fromTo(
          split.chars,
          {
            rotationX: -90
          },
          {
            rotationX: 90,
            duration: this.animationTime,
            ease: 'none',
            transformOrigin: transformOrigin,
            stagger: {
              each: stagger
            }
          },
          index === 0 ? '>-130%' : `>-${totalCharsAnimationDuration * 0.8}`
        );

        this.timeline.to(
          container,
          {
            keyframes: [
              { opacity: 1, duration: totalCharsAnimationDuration * 0.05, ease: 'none' },
              { opacity: 1, duration: totalCharsAnimationDuration * 0.90, ease: 'none' },
              { opacity: 0, duration: totalCharsAnimationDuration * 0.05, ease: 'none' }
            ],
            immediateRender: false
          },
          '<'
        );

        this.timeline.to(
          split.chars,
          {
            keyframes: [
              { opacity: 1, duration: this.animationTime * 0.99, ease: 'none' },
              { opacity: 0, duration: this.animationTime * 0.01 }
            ],
            stagger: {
              each: stagger
            }
          },
          '<'
        );
      });

      // final main layers
      this.timeline.set(
        splitMainText.chars,
        {
          rotationX: -90
        },
        `>-${totalCharsAnimationDuration * 0.7}`
      );

      this.timeline.fromTo(
        splitMainText.chars,
        {
          rotationX: -90
        },
        {
          rotationX: 0,
          stagger: { each: stagger },
          duration: this.animationTime * 0.5,
          ease: 'none',
          transformOrigin: `50% 50% ${depth}`,
          immediateRender: false
        },
        `<`
      );
    }

    bindEvents() {
      this.trigger.addEventListener('mouseenter', () => this.timeline.seek(this.animationTime * 0.3).play());
      this.trigger.addEventListener('mouseleave', () => this.timeline.reverse());
    }
  }

  customElements.define('rolling-text', RollingText);
}
